import { useState } from "react";
import { useAdvantageProfile } from "@/hooks/use-mock-data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Save, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function ProfilePage() {
  const { data: profile } = useAdvantageProfile();
  const [industries, setIndustries] = useState(profile.industriesKnown);
  const [skills, setSkills] = useState(profile.skills);
  const [channels, setChannels] = useState(profile.distributionChannels);
  const [budget, setBudget] = useState(profile.budgetMonthlyMax);
  const [newSkill, setNewSkill] = useState("");

  const handleSave = () => {
    toast.success("Excellent, Commander. Advantage Profile updated. Scores will be recalculated.", {
      style: { borderLeft: "3px solid #44F2D2" },
    });
  };

  const addSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill("");
    }
  };

  return (
    <div className="animate-fade-in max-w-3xl mx-auto px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-brand text-4xl tracking-wide">ADVANTAGE PROFILE</h1>
        <Button onClick={handleSave} className="bg-electric-blue hover:bg-electric-blue/90 text-white text-xs uppercase tracking-wider">
          <Save className="w-4 h-4 mr-1" /> Save Profile
        </Button>
      </div>

      {/* Industries Known */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">INDUSTRIES KNOWN</h2>
        <p className="text-xs text-muted-foreground mb-4">Rate your proximity to each industry (1 = unknown, 10 = deep expertise)</p>
        <div className="space-y-3">
          {industries.map((ind, i) => (
            <div key={ind.name} className="glass-card rounded-lg p-3 flex items-center gap-4">
              <span className="text-sm text-foreground w-40 shrink-0">{ind.name}</span>
              <Slider
                value={[ind.proximityRating]}
                onValueChange={([val]) => {
                  const updated = [...industries];
                  updated[i] = { ...ind, proximityRating: val! };
                  setIndustries(updated);
                }}
                max={10}
                step={1}
                className="flex-1"
              />
              <span className="font-mono text-sm w-6 text-right text-electric-blue">{ind.proximityRating}</span>
              <button
                onClick={() => setIndustries(industries.filter((_, j) => j !== i))}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Skills */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">SKILLS</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          {skills.map((skill) => (
            <span
              key={skill}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[rgba(255,255,255,0.06)] text-xs text-foreground border border-[rgba(255,255,255,0.08)]"
            >
              {skill}
              <button onClick={() => setSkills(skills.filter((s) => s !== skill))} className="text-muted-foreground hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSkill()}
            placeholder="Add a skill..."
            className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] max-w-xs"
          />
          <Button onClick={addSkill} variant="outline" size="sm" className="border-[rgba(255,255,255,0.08)]">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Distribution Channels */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">DISTRIBUTION CHANNELS</h2>
        <p className="text-xs text-muted-foreground mb-4">Rate strength of each channel (1 = weak, 10 = dominant)</p>
        <div className="space-y-3">
          {channels.map((ch, i) => (
            <div key={ch.name} className="glass-card rounded-lg p-3 flex items-center gap-4">
              <span className="text-sm text-foreground w-48 shrink-0">{ch.name}</span>
              <Slider
                value={[ch.strength]}
                onValueChange={([val]) => {
                  const updated = [...channels];
                  updated[i] = { ...ch, strength: val! };
                  setChannels(updated);
                }}
                max={10}
                step={1}
                className="flex-1"
              />
              <span className="font-mono text-sm w-6 text-right text-mint">{ch.strength}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Products Shipped */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">PRODUCTS SHIPPED</h2>
        <div className="space-y-2">
          {profile.productsShipped.map((p) => (
            <div key={p.name} className="glass-card rounded-lg p-3 flex items-center justify-between">
              <div>
                <span className="text-sm text-foreground">{p.name}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-[rgba(255,255,255,0.06)] px-1.5 py-0.5 rounded">{p.category}</span>
              </div>
              <span className="text-xs text-muted-foreground font-mono">
                {new Date(p.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Budget */}
      <section className="mb-8">
        <h2 className="font-brand text-xl tracking-wide mb-3">MONTHLY VENTURE BUDGET</h2>
        <div className="flex items-center gap-3">
          <span className="font-mono text-lg text-foreground">$</span>
          <Input
            type="number"
            value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
            className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] font-mono max-w-[200px]"
          />
          <span className="text-xs text-muted-foreground">/month max for new ventures</span>
        </div>
      </section>
    </div>
  );
}
